import styled, { css, keyframes } from "styled-components";

// Design tokens shared with the extension's side panel and options page
// ("Shopping Assistant" design), scoped to the dashboard page.
export const Page = styled.div`
  --acc: oklch(0.5 0.15 280);
  --acc-ink: oklch(0.99 0.01 280);
  --acc-soft: oklch(0.95 0.03 280);
  --acc-mid: oklch(0.72 0.1 280);
  --bad: oklch(0.48 0.17 25);
  --good: oklch(0.55 0.13 152);
  --warn: oklch(0.62 0.13 68);
  --ink: oklch(0.23 0.018 275);
  --ink2: oklch(0.42 0.016 275);
  --ink3: oklch(0.55 0.014 275);
  --line: oklch(0.9 0.008 275);
  --page: oklch(0.985 0.003 275);
  --surface: oklch(1 0 0);
  --surface2: oklch(0.965 0.005 275);

  min-height: 100vh;
  background: var(--page);
  color: var(--ink);
  font-family: "IBM Plex Sans", system-ui, "Segoe UI", sans-serif;
  font-size: 13px;
  line-height: 1.4;
  -webkit-font-smoothing: antialiased;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  button,
  input {
    font: inherit;
    color: inherit;
  }

  button {
    cursor: pointer;
  }

  @media (max-width: 600px) {
    .hide-sm {
      display: none;
    }
  }
`;

// Material Symbols glyph, as used throughout the design.
export const Icon = ({ name, size = 18, fill, weight = 300, style }) => (
  <span
    aria-hidden="true"
    style={{
      font: `${weight} ${size}px/1 'Material Symbols Outlined'`,
      fontVariationSettings: fill ? "'FILL' 1" : undefined,
      flex: "none",
      ...style,
    }}
  >
    {name}
  </span>
);

export const rupees = (value) =>
  `₹${Math.round(Number(value) || 0).toLocaleString("en-IN")}`;

export const Card = styled.div`
  padding: ${(props) => props.$pad ?? "16px 18px"};
  border: 1px solid var(--line);
  border-radius: 14px;
  background: var(--surface);
  min-width: 0;
`;

export const CardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  flex-wrap: wrap;

  & > span:first-child {
    flex: 1;
    font-size: 14px;
    font-weight: 600;
  }
`;

export const SectionTitle = ({ title, sub, children }) => (
  <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
    <div style={{ flex: 1, minWidth: 220 }}>
      <h2 style={{ fontSize: 19, fontWeight: 600, margin: 0, letterSpacing: "-.01em" }}>{title}</h2>
      {sub && <p style={{ fontSize: 13, color: "var(--ink2)", margin: "4px 0 0" }}>{sub}</p>}
    </div>
    {children}
  </div>
);

const SegTrack = styled.div`
  display: flex;
  padding: 3px;
  gap: 2px;
  background: var(--surface2);
  border-radius: 8px;
  flex: none;
`;

const SegButton = styled.button`
  border: 0;
  height: 28px;
  padding: 0 11px;
  border-radius: 6px;
  font-size: 12.5px;
  font-weight: 500;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
  background: ${(props) => (props.$active ? "var(--surface)" : "transparent")};
  color: ${(props) => (props.$active ? "var(--ink)" : "var(--ink2)")};
  box-shadow: ${(props) => (props.$active ? "0 1px 2px oklch(0 0 0 / .12)" : "none")};
`;

export const Segmented = ({ options, value, onChange }) => (
  <SegTrack>
    {options.map((option) => (
      <SegButton
        key={option.value}
        $active={option.value === value}
        onClick={() => onChange(option.value)}
      >
        {option.icon && <Icon name={option.icon} size={14} weight={400} />}
        {option.label}
      </SegButton>
    ))}
  </SegTrack>
);

export const OutlineButton = styled.button`
  height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--line);
  background: var(--surface);
  font-size: 12.5px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: var(--ink3);
  }
`;

export const PrimaryButton = styled.button`
  height: 36px;
  padding: 0 14px;
  border: 0;
  border-radius: 9px;
  background: var(--acc);
  color: var(--acc-ink);
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover:not(:disabled) {
    filter: brightness(1.08);
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

const pulse = keyframes`
  50% {
    opacity: 0.55;
  }
`;

export const Skeleton = styled.div`
  height: ${(props) => props.$h ?? 14}px;
  width: ${(props) => props.$w ?? "100%"};
  border-radius: 6px;
  background: var(--surface2);
  animation: ${pulse} 1.4s ease-in-out infinite;
  ${(props) =>
    props.$block &&
    css`
      border-radius: 14px;
    `}
`;

export const Empty = ({ icon, title, children }) => (
  <div
    style={{
      padding: "28px 16px",
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 6,
      color: "var(--ink2)",
      fontSize: 12.5,
    }}
  >
    <span
      style={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        background: "var(--acc-soft)",
        color: "var(--acc)",
        display: "grid",
        placeItems: "center",
      }}
    >
      <Icon name={icon} size={20} weight={400} />
    </span>
    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>{title}</div>
    <div style={{ maxWidth: 340 }}>{children}</div>
  </div>
);
